/**
 * One measure gesture, written as a Stand of its own — the path „Maß“
 * (`WallMeasureField`) and the finding of a figure the sketch contradicts
 * (`ImplausibleMeasure`, E-64) share, so the two cannot write a measure two
 * ways: the board's own pending write first, the post, the plan and the
 * Vorgang read again, and the 409 „Stand veraltet" handed to the board.
 *
 * What a caller shows for the outcome is its own; what happened is decided
 * here, once.
 */
import { useQueryClient } from "@tanstack/react-query";

import { CASE_KEY, CASES_KEY, postVersion, StaleVersionError } from "@/api";
import { ProblemError, type components } from "@k60/contracts";
import { refusedAsCancelled } from "@/lib/facadeEditability";
import { formatMetres, formatMm } from "@/lib/format";

type PlanningStateWire = components["schemas"]["PlanningStateIn"];
type OpIn = components["schemas"]["OpIn"];

/** What one measure gesture states about a wall. */
export type MeasureGesture =
  /** A figure typed into „Maß“. */
  | { kind: "set"; mm: number }
  /** „Maß entfernen“. */
  | { kind: "remove" }
  /** A comma shift offered for a figure the sketch contradicts (E-64): `mm`
   * in place of the stated `fromMm`. */
  | { kind: "shift"; mm: number; fromMm: number }
  /** „Stimmt so“ (E-64): the stated `mm` stands although the sketch draws the
   * wall otherwise. */
  | { kind: "confirm"; mm: number };

/**
 * What a stale Stand cost — the wall and the figure that did not land, `mm:
 * null` for „Maß entfernen“. The board's reload closes the field or the
 * finding the gesture came from; the board's own notice names both, so the
 * number does not vanish without a word (screen check 15.10.2026, V-3).
 */
export interface LostMeasure {
  wallName: string;
  mm: number | null;
}

/** How a measure gesture ended. */
export type MeasureWriteOutcome =
  /** Written; the plan and the Vorgang were read again (or the wait ran out). */
  | { kind: "saved" }
  /** A reload is on its way: nothing was posted. */
  | { kind: "held" }
  /** Somebody else's Stand landed first — handed to `onStale` already. */
  | { kind: "stale" }
  /** The Vorgang was cancelled meanwhile; the Vorgang is read again. */
  | { kind: "cancelled" }
  /** 422: the server refused the value — its sentence, for the field. */
  | { kind: "invalid"; message: string }
  /** Any other refusal or failure — the sentence to show, complete. */
  | { kind: "refused"; message: string };

export interface MeasureTarget {
  caseId: string;
  /** The document's own id (`w1`, …) — what every op is keyed by. */
  wallId: string;
  /** „Wand 3“ — how the screen names the wall, in the Stand's summary. */
  wallName: string;
  /** The lock this gesture posts against, unless {@link settle} says another. */
  expectedVersionNo: number;
  /** The board as it stands — a Stand is the whole board, never a diff. */
  planningState: PlanningStateWire;
  /**
   * Writes the board still holds go first, and the number to post against
   * comes back — or `null` where a reload is on its way and nothing may be
   * written. Without it the gesture posts against {@link expectedVersionNo}.
   */
  settle?: () => Promise<number | null>;
  /** The new Stand is written; the plan is asked for again here. */
  onSaved: (versionNo: number) => void;
  /** The gesture is on the wire (`true`), and has come back (`false`). */
  onWriting?: (writing: boolean) => void;
  /** Somebody else's Stand landed first — the board's own 409 handling. */
  onStale: (cause: StaleVersionError, lost: LostMeasure) => void;
}

/** How long a gesture waits for the plan and the Vorgang read again after its Stand (N-2). */
const REREAD_WAIT_MS = 3000;

function opsOf(wallId: string, gesture: MeasureGesture): OpIn[] {
  switch (gesture.kind) {
    case "set":
    case "shift":
      return [{ op: "override_length", wallId, lengthMm: gesture.mm }];
    case "remove":
      return [{ op: "declare_unmeasured", wallId }];
    case "confirm":
      return [{ op: "confirm_length", wallId }];
  }
}

/**
 * The Stand's line in the record. The wall as the screen names it, never the
 * document's id: the record shows this line for good, and a Basisstand
 * renumbers the ids (`w4` there is not the wall that was `w4` here). A comma
 * shift and a confirmation say both figures in metres, the way the finding
 * put the question (R-9: the line is the record of the decision).
 */
export function measureSummary(wallName: string, gesture: MeasureGesture): string {
  switch (gesture.kind) {
    case "set":
      return `Maß ${wallName} auf ${formatMm(gesture.mm)}`;
    case "remove":
      return `Maß ${wallName} entfernt`;
    case "shift":
      return `${wallName}: ${formatMetres(gesture.mm)} statt ${formatMetres(gesture.fromMm)}`;
    case "confirm":
      return `${wallName}: ${formatMetres(gesture.mm)} bestätigt`;
  }
}

function lostOf(wallName: string, gesture: MeasureGesture): LostMeasure {
  return { wallName, mm: gesture.kind === "remove" ? null : gesture.mm };
}

/**
 * Writes one measure gesture for `target` — returned as a function so a
 * caller asks its own questions (the release, a figure no house has) before
 * anything goes out.
 *
 * **Not optimistic.** A length moves the whole drawing, and only the server
 * knows where to: the gesture is posted, the plan and the Vorgang read again
 * — at most {@link REREAD_WAIT_MS} (re-review of 41503767, N-2), a reading
 * that does not answer must not hold the board for good — and only then is
 * it `saved`.
 */
export function useMeasureWrite(target: MeasureTarget): (gesture: MeasureGesture) => Promise<MeasureWriteOutcome> {
  const client = useQueryClient();
  const { caseId, wallId, wallName, expectedVersionNo, planningState, settle, onSaved, onWriting, onStale } = target;

  return async (gesture) => {
    let posted = false;
    try {
      // Inside the `try` (whole-branch review): a board write that fails while
      // this gesture waits for it must not leave the caller locked for good.
      const expected = settle ? await settle() : expectedVersionNo;
      // A reload is on its way (a 409 of the board's own): the board says so,
      // and this gesture would be made on ground that is about to go.
      if (expected === null) return { kind: "held" };
      posted = true;
      onWriting?.(true);
      const { versionNo } = await postVersion(caseId, {
        expectedVersionNo: expected,
        ops: opsOf(wallId, gesture),
        planningState,
        changeSummary: measureSummary(wallName, gesture),
      });
      onSaved(versionNo);
      // The drawing, the findings, the Vorgang's status and its calculation
      // all moved with the Stand — read again before the caller lets go, the
      // board held meanwhile (`onWriting`).
      const reread = Promise.all([
        client.invalidateQueries({ queryKey: ["case", caseId, "plan"] }),
        client.invalidateQueries({ queryKey: CASE_KEY(caseId) }),
        // A released or calculated Vorgang is „In Planung“ again (D-11): the
        // start page's row and the register's too — not only after a release,
        // a calculation's stage moves just the same.
        client.invalidateQueries({ queryKey: CASES_KEY }),
      ]);
      let timer: number | undefined;
      try {
        await Promise.race([
          reread,
          new Promise<void>((resolve) => {
            timer = window.setTimeout(resolve, REREAD_WAIT_MS);
          }),
        ]);
      } finally {
        window.clearTimeout(timer);
      }
      return { kind: "saved" };
    } catch (cause: unknown) {
      if (cause instanceof StaleVersionError) {
        onStale(cause, lostOf(wallName, gesture));
        return { kind: "stale" };
      }
      if (refusedAsCancelled(cause)) {
        void client.invalidateQueries({ queryKey: CASE_KEY(caseId) });
        return { kind: "cancelled" };
      }
      if (cause instanceof ProblemError && cause.status === 422) {
        return { kind: "invalid", message: cause.userMessage };
      }
      if (cause instanceof ProblemError && cause.status === 409) {
        // A sent Vorgang, above all: the server's own sentence, and the
        // Vorgang read again so the caller gives way to its hint.
        void client.invalidateQueries({ queryKey: CASE_KEY(caseId) });
        return { kind: "refused", message: cause.userMessage };
      }
      const why =
        cause instanceof ProblemError ? cause.userMessage : cause instanceof Error ? cause.message : String(cause);
      return { kind: "refused", message: `Nicht gespeichert — ${why}` };
    } finally {
      if (posted) onWriting?.(false);
    }
  };
}
